import React, { useState } from 'react';
import './WhatsAppLinkGenerator.css';
import usePageMeta from '../../hooks/usePageMeta';

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface MessageTemplate {
  name: string;
  message: string;
}

interface HistoryItem {
  id: number;
  link: string;
  phone: string;
  message: string;
  timestamp: string;
  contactName: string;
}

// Componentes de ícones SVG simples
const CopyIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

const PhoneIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const MessageIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
  </svg>
);

const ShareIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="18" cy="5" r="3"></circle>
    <circle cx="6" cy="12" r="3"></circle>
    <circle cx="18" cy="19" r="3"></circle>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3,6 5,6 21,6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const WhatsAppLinkGenerator: React.FC = () => {
  usePageMeta({
    title: 'Gerador de Link para WhatsApp Grátis',
    description:
      'Crie links de WhatsApp com mensagem pronta em segundos, gratuitamente. Gere o link, o QR Code e compartilhe com seus clientes.',
    path: '/free-tools/whatsapp-link-generator',
  });

  const [countryCode, setCountryCode] = useState<string>('+55');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [generatedLink, setGeneratedLink] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [qrCode, setQrCode] = useState<string>('');
  const [linkHistory, setLinkHistory] = useState<HistoryItem[]>([]);
  const [contactName, setContactName] = useState<string>('');

  // Lista de países
  const countries: Country[] = [
    { code: '+55', name: 'Brasil', flag: '🇧🇷' },
    { code: '+1', name: 'EUA/Canadá', flag: '🇺🇸' },
    { code: '+44', name: 'Reino Unido', flag: '🇬🇧' },
    { code: '+33', name: 'França', flag: '🇫🇷' },
    { code: '+49', name: 'Alemanha', flag: '🇩🇪' },
    { code: '+34', name: 'Espanha', flag: '🇪🇸' },
    { code: '+39', name: 'Itália', flag: '🇮🇹' },
    { code: '+54', name: 'Argentina', flag: '🇦🇷' },
    { code: '+52', name: 'México', flag: '🇲🇽' },
    { code: '+351', name: 'Portugal', flag: '🇵🇹' },
    { code: '+86', name: 'China', flag: '🇨🇳' },
    { code: '+81', name: 'Japão', flag: '🇯🇵' },
    { code: '+91', name: 'Índia', flag: '🇮🇳' },
  ];

  // Templates de mensagem
  const messageTemplates: MessageTemplate[] = [
    {
      name: 'Atendimento Comercial',
      message: 'Olá! 👋\n\nGostaria de saber mais informações sobre seus produtos/serviços.\n\nPoderia me ajudar?'
    },
    {
      name: 'Agendamento',
      message: 'Oi! 😊\n\nGostaria de agendar um horário.\n\nQuais são as disponibilidades?'
    },
    {
      name: 'Suporte Técnico',
      message: 'Olá! 🛠️\n\nEstou precisando de ajuda técnica.\n\nPodem me auxiliar?'
    },
    {
      name: 'Orçamento',
      message: 'Bom dia! 💰\n\nGostaria de solicitar um orçamento.\n\nQuando podemos conversar?'
    }
  ];

  // Gerar link do WhatsApp
  const generateLink = (): void => {
    if (!phoneNumber.trim()) {
      alert('Por favor, insira o número de telefone.');
      return;
    }

    const cleanPhone = phoneNumber.replace(/\D/g, '');
    const formattedMessage = encodeURIComponent(message.trim());
    const baseLink = `https://wa.me/${countryCode.replace('+', '')}${cleanPhone}`;
    const fullLink = message.trim() ? `${baseLink}?text=${formattedMessage}` : baseLink;
    
    setGeneratedLink(fullLink);
    generateQRCode(fullLink);
    saveToHistory(fullLink);
    setCopied(false);
  };

  // Gerar QR Code
  const generateQRCode = (link: string): void => {
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(link)}`;
    setQrCode(qrApiUrl);
  };

  // Copiar para área de transferência
  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = generatedLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Salvar no histórico
  const saveToHistory = (link: string): void => {
    const newEntry: HistoryItem = {
      id: Date.now(),
      link,
      phone: `${countryCode} ${phoneNumber}`,
      message: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
      timestamp: new Date().toLocaleString('pt-BR'),
      contactName: contactName || 'Sem nome'
    };
    
    setLinkHistory(prev => [newEntry, ...prev.slice(0, 4)]);
  };

  // Aplicar template de mensagem
  const applyTemplate = (template: MessageTemplate): void => {
    setMessage(template.message);
  };

  // Limpar formulário
  const clearForm = (): void => {
    setPhoneNumber('');
    setMessage('');
    setContactName('');
    setGeneratedLink('');
    setQrCode('');
    setCopied(false);
  };

  // Compartilhar link
  const shareLink = async (): Promise<void> => {
    if (navigator.share && generatedLink) {
      try {
        await navigator.share({
          title: 'Link do WhatsApp',
          text: 'Clique no link para conversar no WhatsApp',
          url: generatedLink
        });
      } catch (err) {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  // Formatação para preview - CORRIGIDO
  const renderPreviewMessage = (): React.ReactNode => {
    if (!message.trim()) {
      return <div className="message-bubble empty">Sua mensagem aparecerá aqui...</div>;
    }
    
    return (
      <div className="message-bubble">
        {message}
        <div className="message-time">{getCurrentTime()}</div>
      </div>
    );
  };

  // Formatação do número de telefone
  const formatPhoneNumber = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    
    if (countryCode === '+55') {
      // Formato brasileiro
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
      if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
    
    return numbers;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setPhoneNumber(formatPhoneNumber(value));
  };

  const handleHistoryClick = (item: HistoryItem): void => {
    setGeneratedLink(item.link);
    generateQRCode(item.link);
  };

  const getCurrentTime = (): string => {
    return new Date().toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="whatsapp-link-generator">
      <div className="generator-card">
        <div className="generator-left">
          <h1 className="generator-title">Link WhatsApp</h1>
          <p className="generator-subtitle">
            Crie links personalizados para WhatsApp de forma rápida e fácil
          </p>
          
          <div className="form-group">
            <label className="form-label">Nome do contato (opcional)</label>
            <input
              type="text"
              className="form-input"
              placeholder="Nome para identificação"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Telefone</label>
            <div className="input-row">
              <select
                className="form-select"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="form-input"
                placeholder="(11) 99999-9999"
                value={phoneNumber}
                onChange={handlePhoneChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Mensagem</label>
            <textarea
              className="form-textarea"
              placeholder="Digite sua mensagem aqui... Use quebras de linha para organizar o texto"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            
            <div className="template-grid">
              {messageTemplates.map((template, index) => (
                <button
                  key={index}
                  className="template-button"
                  onClick={() => applyTemplate(template)}
                  type="button"
                >
                  {template.name}
                </button>
              ))}
            </div>
          </div>

          <button className="primary-button" onClick={generateLink} type="button">
            <MessageIcon />
            Gerar Link do WhatsApp
          </button>

          {generatedLink && (
            <>
              <div className="link-box">
                {generatedLink}
              </div>
              
              <div className="button-group">
                <button 
                  className={`copy-button ${copied ? 'copied' : 'default'}`} 
                  onClick={copyToClipboard}
                  type="button"
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
                
                <button className="secondary-button" onClick={shareLink} type="button">
                  <ShareIcon />
                  Compartilhar
                </button>
                
                <button className="secondary-button" onClick={clearForm} type="button">
                  <TrashIcon />
                  Limpar
                </button>
              </div>
            </>
          )}

          {linkHistory.length > 0 && (
            <div className="history-section">
              <label className="form-label">Histórico recente</label>
              {linkHistory.map(item => (
                <div
                  key={item.id}
                  className="history-item"
                  onClick={() => handleHistoryClick(item)}
                >
                  <div className="history-contact-name">{item.contactName}</div>
                  <div className="history-phone">{item.phone}</div>
                  <div className="history-message">{item.message}</div>
                  <div className="history-time">{item.timestamp}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="generator-right">
          <div className="phone-preview">
            <div className="phone-screen">
              <div className="whatsapp-header">
                <div className="contact-avatar">
                  <PhoneIcon />
                </div>
                <div className="contact-info">
                  <h3 className="contact-name">
                    {contactName || 'Contato'}
                  </h3>
                  <p className="contact-phone">
                    {phoneNumber ? `${countryCode} ${phoneNumber}` : 'Número de telefone'}
                  </p>
                </div>
              </div>
              
              <div className="chat-area">
                {renderPreviewMessage()}
              </div>
            </div>
          </div>

          {qrCode && (
            <div className="qr-section">
              <h3 className="qr-title">QR Code</h3>
              <p className="qr-description">
                Escaneie para abrir no WhatsApp
              </p>
              <img src={qrCode} alt="QR Code do WhatsApp" className="qr-code" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppLinkGenerator;